import type { HeadingRecord } from '@scalar-org/entities/publish'
import rehypeFormat from 'rehype-format'
import rehypeHighlight from 'rehype-highlight'
import rehypeParse from 'rehype-parse'
import rehypeRaw from 'rehype-raw'
import rehypeRemark from 'rehype-remark'
import rehypeRemoveComments from 'rehype-remove-comments'
import rehypeStringify from 'rehype-stringify'
import remarkDirective from 'remark-directive'
import remarkFrontmatter from 'remark-frontmatter'
import remarkGfm from 'remark-gfm'
import remarkParse from 'remark-parse'
import remarkRehype from 'remark-rehype'
import remarkStringify from 'remark-stringify'
import { unified } from 'unified'
import { addClassNames, customComponentsPlugin } from '@/components'
import { visit } from 'unist-util-visit'

import {
  customHtmlInMarkdownPlugin,
  extractHeadings,
} from './parse-embedded-html'
import { customMarkdownPlugin } from './parse-directives'
import { unwrapPreTags } from './unwrap-pre-tags'
import { nestedScalarTabs } from './process-nested-children'
import { removeEmptyPTags } from './remove-empty-p-tags'
import { hoistScalarElements } from '@/markdown/hoist-scalar-elements'

export async function htmlFromMarkdown(args: {
  md: string
  pageTitle?: string
  pageDescription?: string
  headings?: Record<string, HeadingRecord>
  logger?: (tree: any) => void
}) {
  const headings = args.headings || {}
  const { md, pageTitle, pageDescription, logger: _logger } = args
  const pipeline = unified()
    /** Parse the markdown into an M-AST tree */
    .use(remarkParse)
    /** Parse frontmatter from the markdown */
    .use(remarkFrontmatter)
    /** Support GFM markdown */
    .use(remarkGfm)
    /** Parse directives into the M-AST tree */
    .use(remarkDirective)
    /**
     * Extends element properties so that they can be converted into Scalar custom elements
     * At this point we must differentiate between elements that may share the same html tag name
     * For example, code blocks may be inline or fenced.
     *
     * This will:
     * 1. Change the node tag type to match a scalar element tagName if a matching directive is found
     * 2. Map the directive data to the attributes model of a matching scalar element
     * 3. Validate the attributes and remove the node if the attrs are invalid
     */
    .use(customMarkdownPlugin())
    /** Convert from M-AST to H-AST */
    .use(remarkRehype, {
      allowDangerousHtml: true,
    })
    /** Hoist scalar custom HTML elements to their proper positions. */
    .use(hoistScalarElements())
    /** Parse embedded HTML from the markdown tree */
    .use(rehypeRaw)
    /**
     * Code block nodes get wrapped in a pre tag. Because we will replace this element
     * later with the rendered one we need to unwrap the pre tags
     */
    .use(unwrapPreTags())
    /**
     * Process scalar-tab elements to set them as children of scalar-tabs
     */
    .use(nestedScalarTabs())
    //.use(logTree('after nestedScalarTabs'))
    /**
     * Transform some html nodes to support scalar replacement rendering
     *
     * This will:
     * 1. Look for any scalar- tags that have a matching scalar element
     * 2. Map the data attrs of the node to the attributes model of a matching scalar element
     * 3. Validate the attributes and remove the node if the attrs are invalid
     */
    .use(customHtmlInMarkdownPlugin({ pageTitle, pageDescription }))
    /** Parse the heading tree for the table of contents. Edits in place */
    .use(extractHeadings(headings))
    /** Some elements we just apply scalar classes to */
    .use(addClassNames())
    /** Render text extension placeholders. This replaces any custom_<n> tags with rendered ScalarComponents */
    .use(customComponentsPlugin({}, pageTitle || '', pageDescription || ''))
    /** Run lowlight syntax highlighting. Code blocks require a language class */
    .use(rehypeHighlight)
    /** Remove comments from the html */
    .use(rehypeRemoveComments)
    /** Remove all empty paragraph tags from the document */
    .use(removeEmptyPTags())
    /** Format the html whitespace */
    .use(rehypeFormat)
    /** Convert the html tree back to a string */
    .use(rehypeStringify)

  try {
    return String(await pipeline.process(md))
  } catch (e) {
    console.error('Error processing markdown', e)
    return String(md)
  }
}

export function getNodeLogger(logger: (node: any) => void) {
  return (tree: any) => {
    logger('--- AST ---')
    visit(tree, (node) => {
      logger(node)
    })
  }
}

/** Transform static HTML into markdown */
export async function markdownFromHtml(html: string) {
  return String(
    await unified()
      .use(rehypeParse, { fragment: true })
      .use(rehypeRemoveComments)
      .use(rehypeRemark)
      .use(remarkGfm)
      .use(remarkStringify)
      .process(html),
  )
}

/**
 * Process a HAST tree through the standard pipeline starting after rehypeRaw (line 73)
 * This is useful for debugging specific parts of the tree transformation process
 *
 * @param hastTree The HAST tree to process (must be a valid HAST tree)
 * @param options Processing options like pageTitle, pageDescription, and headings
 * @returns Processed HTML string
 */
export const processHastFromRehypeRaw = async (
  hastTree: any,
  options: {
    pageTitle?: string
    pageDescription?: string
    headings?: Record<string, HeadingRecord>
  } = {},
): Promise<string> => {
  const { pageTitle = '', pageDescription = '', headings = {} } = options

  try {
    // Ensure we have a proper root node
    const rootNode =
      hastTree.type === 'root'
        ? hastTree
        : {
            type: 'root' as const,
            children: Array.isArray(hastTree) ? hastTree : [hastTree],
          }

    // Create a pipeline that continues from rehypeRaw
    const pipeline = unified()

    pipeline
      .use(hoistScalarElements())
      .use(rehypeRaw)
      .use(unwrapPreTags())
      .use(nestedScalarTabs())
      .use(customHtmlInMarkdownPlugin({ pageTitle, pageDescription }))
      .use(extractHeadings(headings))
      .use(addClassNames())
      .use(customComponentsPlugin({}, pageTitle || '', pageDescription || ''))
      .use(rehypeHighlight)
      .use(rehypeRemoveComments)
      .use(removeEmptyPTags())
      .use(rehypeFormat)
      .use(rehypeStringify)

    // Run the node through the pipeline
    const file = await pipeline.run(rootNode)
    return String(pipeline.stringify(file))
  } catch (e) {
    console.error('Error processing HAST from rehypeRaw:', e)
    const errorMessage = e instanceof Error ? e.message : String(e)
    return `<div class="error">Error processing HAST from rehypeRaw: ${errorMessage}</div>`
  }
}
